// Lazy Gravity Forms for the footer modals (see inc/relaunch/forms.php).
// A placeholder <div data-amt-lazy-form> replaces the form on relaunch pages; the
// first time its modal gains the `show` class we fetch the markup plus the exact
// asset tags GF would have printed, load them in order, then run the init scripts.

const loaded = new WeakSet();
const consentAllowed = (element) => {
	const service = element.getAttribute('data-amt-consent-service');
	return service ? window.AftermetooConsent?.hasConsent(service) === true : window.CookieCare?.hasConsent('functional') === true;
};
const requiresConsent = (placeholder) => placeholder.hasAttribute('data-amt-consent-service') || placeholder.hasAttribute('data-amt-functional-form');
const canLoad = (placeholder) => !requiresConsent(placeholder) || consentAllowed(placeholder);
const requestConsent = () => window.AftermetooConsent ? window.AftermetooConsent.openSettings() : document.dispatchEvent(new CustomEvent('cookiecare:request'));

function syncFormConsent() {
	document.querySelectorAll('[data-amt-form-consent]').forEach((notice) => { notice.hidden = consentAllowed(notice); });
}

function requireConsent(placeholder) {
	if (!canLoad(placeholder)) throw new Error('amt-form-consent-required');
}

const hasTag = (id) => Boolean(id && document.getElementById(id));

function runScript(node) {
	return new Promise((resolve, reject) => {
		if (hasTag(node.id)) return resolve();
		const el = document.createElement('script');
		[...node.attributes].forEach((a) => el.setAttribute(a.name, a.value));
		if (el.getAttribute('type') === 'text/plain' && el.hasAttribute('data-usercentrics')) {
			// The server also blocks CAPTCHA assets in lazy responses. Activate
			// only this known service after the shared CMP state confirmed it;
			// waiting on an inert script's load event would never finish.
			const service = el.getAttribute('data-usercentrics');
			if (!['reCAPTCHA', 'Google reCAPTCHA'].includes(service)) return reject(new Error('amt-form-service-unavailable'));
			if (window.AftermetooConsent?.hasConsent('recaptcha') !== true) return reject(new Error('amt-form-consent-required'));
			el.setAttribute('type', 'text/javascript');
		}
		el.removeAttribute('defer');
		if (node.src) {
			el.async = false;
			el.onload = resolve;
			// A missing optional add-on bundle must not leave the modal empty.
			el.onerror = () => { console.warn('Lazy form: failed to load ' + node.src); resolve(); };
		} else {
			el.textContent = node.textContent;
		}
		document.body.appendChild(el);
		if (!node.src) resolve();
	});
}

function addStyle(node) {
	return new Promise((resolve) => {
		if (hasTag(node.id)) return resolve();
		const el = node.cloneNode(true);
		if (el.tagName === 'LINK') {
			el.onload = el.onerror = resolve;
			document.head.appendChild(el);
		} else {
			document.head.appendChild(el);
			resolve();
		}
	});
}

async function loadForm(placeholder) {
	requireConsent(placeholder);
	const url = new URL(placeholder.dataset.amtLazyFormUrl, location.href);
	url.searchParams.set('amt_page', location.pathname + location.search);
	const res = await fetch(url, { credentials: 'same-origin' });
	const json = await res.json();
	if (!json.success) throw new Error('Form unavailable');
	requireConsent(placeholder);
	const parse = (html) => new DOMParser().parseFromString(html, 'text/html');

	await Promise.all([...parse(json.data.styles).querySelectorAll('link, style')].map(addStyle));
	for (const node of parse(json.data.scripts).querySelectorAll('script')) {
		requireConsent(placeholder);
		await runScript(node);
	}
	// GF's hooks shim gates initializeOnLoaded() on DOMContentLoaded and on the
	// gform_main_scripts_loaded / theme events; the scripts just ran, DOM is long ready.
	const ready = () => window.gform && Object.assign(window.gform, { domLoaded: true, scriptsLoaded: true, themeScriptsLoaded: true });
	ready();
	const doc = parse(json.data.html);
	const inline = [...doc.querySelectorAll('script')];
	inline.forEach((s) => s.remove());
	placeholder.innerHTML = doc.body.innerHTML;
	for (const node of inline) {
		requireConsent(placeholder);
		await runScript(node);
		ready();
	}
	syncFormConsent();
}

function ensureForm(modal) {
	const placeholder = modal.querySelector('[data-amt-lazy-form]');
	if (!placeholder || loaded.has(placeholder)) return;
	if (!canLoad(placeholder)) { syncFormConsent(); return; }
	loaded.add(placeholder);
	loadForm(placeholder).catch((err) => {
		loaded.delete(placeholder);
		if (err.message !== 'amt-form-consent-required') console.error(err);
		syncFormConsent();
	});
}

document.addEventListener('click', (event) => {
	if (event.target.closest('[data-amt-form-cookie-settings]')) {
		event.preventDefault(); requestConsent();
	}
});
// The eager form's local scripts may run before the external API. Prevent a
// confusing invalid-CAPTCHA submission while preserving typed field values.
function guardSubmission(event) {
	const form = event.target.closest('form');
	const formId = form?.id.match(/^gform_(\d+)$/)?.[1];
	const notice = formId && document.querySelector(`[data-amt-form-consent][data-amt-form-id="${formId}"]`);
	if (!notice || consentAllowed(notice)) return;
	if (event.type === 'click' && !event.target.closest('[type="submit"], .gform_next_button')) return;
	event.preventDefault(); event.stopImmediatePropagation(); requestConsent();
}
document.addEventListener('submit', guardSubmission, true);
document.addEventListener('click', guardSubmission, true);
const updateFormConsent = () => {
	syncFormConsent();
	document.querySelectorAll('.modal.show').forEach(ensureForm);
};
document.addEventListener('cookiecare:consent', updateFormConsent);
document.addEventListener('amt:consent', updateFormConsent);
syncFormConsent();

document.querySelectorAll('.modal').forEach((modal) => {
	if (!modal.querySelector('[data-amt-lazy-form]')) return;
	// Modals open from three places in modal.js (click, hash on load, hashchange);
	// watching the class is the one hook that covers all of them.
	new MutationObserver(() => {
		if (modal.classList.contains('show')) ensureForm(modal);
	}).observe(modal, { attributes: true, attributeFilter: ['class'] });
	if (modal.classList.contains('show')) ensureForm(modal);
});
